
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Plane, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Airport } from '@/api/entities';
import { isOfferedCode, loadSearchAirports } from '@/lib/airportSearch';
import { cn } from '@/lib/utils';

// Fallback airports for when Supabase is unavailable
const FALLBACK_AIRPORTS = [
  // United States - Major Cities
  { code: 'JFK', city: 'New York', airport: 'John F. Kennedy International', country: 'United States' },
  { code: 'LGA', city: 'New York', airport: 'LaGuardia', country: 'United States' },
  { code: 'EWR', city: 'Newark', airport: 'Newark Liberty International', country: 'United States' },
  { code: 'LAX', city: 'Los Angeles', airport: 'Los Angeles International', country: 'United States' },
  { code: 'BUR', city: 'Burbank', airport: 'Hollywood Burbank', country: 'United States' },
  { code: 'LGB', city: 'Long Beach', airport: 'Long Beach Airport', country: 'United States' },
  { code: 'SNA', city: 'Santa Ana', airport: 'John Wayne Airport', country: 'United States' },
  { code: 'SFO', city: 'San Francisco', airport: 'San Francisco International', country: 'United States' },
  { code: 'OAK', city: 'Oakland', airport: 'Oakland International', country: 'United States' },
  { code: 'SJC', city: 'San Jose', airport: 'Norman Y. Mineta San José International', country: 'United States' },
  { code: 'ORD', city: 'Chicago', airport: 'O\'Hare International', country: 'United States' },
  { code: 'MDW', city: 'Chicago', airport: 'Midway International', country: 'United States' },
  { code: 'DFW', city: 'Dallas', airport: 'Dallas/Fort Worth International', country: 'United States' },
  { code: 'DAL', city: 'Dallas', airport: 'Dallas Love Field', country: 'United States' },
  { code: 'DEN', city: 'Denver', airport: 'Denver International', country: 'United States' },
  { code: 'ATL', city: 'Atlanta', airport: 'Hartsfield-Jackson International', country: 'United States' },
  { code: 'MIA', city: 'Miami', airport: 'Miami International', country: 'United States' },
  { code: 'FLL', city: 'Fort Lauderdale', airport: 'Fort Lauderdale-Hollywood International', country: 'United States' },
  { code: 'DJT', city: 'West Palm Beach', airport: 'President Donald J. Trump International', country: 'United States' },
  { code: 'LAS', city: 'Las Vegas', airport: 'Harry Reid International', country: 'United States' },
  { code: 'MCO', city: 'Orlando', airport: 'Orlando International', country: 'United States' },
  { code: 'SFB', city: 'Sanford', airport: 'Orlando Sanford International', country: 'United States' },
  { code: 'SEA', city: 'Seattle', airport: 'Seattle-Tacoma International', country: 'United States' },
  { code: 'BFI', city: 'Seattle', airport: 'Boeing Field', country: 'United States' },
  { code: 'CLT', city: 'Charlotte', airport: 'Charlotte Douglas International', country: 'United States' },
  { code: 'PHX', city: 'Phoenix', airport: 'Phoenix Sky Harbor International', country: 'United States' },
  { code: 'AZA', city: 'Mesa', airport: 'Phoenix-Mesa Gateway', country: 'United States' },
  { code: 'IAH', city: 'Houston', airport: 'George Bush Intercontinental', country: 'United States' },
  { code: 'HOU', city: 'Houston', airport: 'William P. Hobby', country: 'United States' },
  { code: 'BOS', city: 'Boston', airport: 'Logan International', country: 'United States' },
  { code: 'DCA', city: 'Washington D.C.', airport: 'Ronald Reagan Washington National', country: 'United States' },
  { code: 'IAD', city: 'Washington D.C.', airport: 'Washington Dulles International', country: 'United States' },
  { code: 'BWI', city: 'Baltimore', airport: 'Baltimore/Washington International', country: 'United States' },
  
  // Hawaii - All Major Airports
  { code: 'HNL', city: 'Honolulu', airport: 'Daniel K. Inouye International Airport', country: 'United States' },
  { code: 'OGG', city: 'Maui', airport: 'Kahului Airport', country: 'United States' },
  { code: 'KOA', city: 'Kona', airport: 'Ellison Onizuka Kona International Airport', country: 'United States' },
  { code: 'ITO', city: 'Hilo', airport: 'Hilo International Airport', country: 'United States' },
  { code: 'LIH', city: 'Lihue', airport: 'Lihue Airport', country: 'United States' },
  { code: 'MKK', city: 'Molokai', airport: 'Molokai Airport', country: 'United States' },
  { code: 'LNY', city: 'Lanai', airport: 'Lanai Airport', country: 'United States' },
  { code: 'JHM', city: 'Kapalua', airport: 'Kapalua Airport', country: 'United States' },
  
  // Alaska
  { code: 'ANC', city: 'Anchorage', airport: 'Ted Stevens Anchorage International Airport', country: 'United States' },
  { code: 'FAI', city: 'Fairbanks', airport: 'Fairbanks International Airport', country: 'United States' },
  { code: 'JNU', city: 'Juneau', airport: 'Juneau International Airport', country: 'United States' },
  { code: 'KTN', city: 'Ketchikan', airport: 'Ketchikan International Airport', country: 'United States' },
  
  // United States - More Cities
  { code: 'SAT', city: 'San Antonio', airport: 'San Antonio International', country: 'United States' },
  { code: 'AUS', city: 'Austin', airport: 'Austin-Bergstrom International', country: 'United States' },
  { code: 'MSP', city: 'Minneapolis', airport: 'Minneapolis-Saint Paul International', country: 'United States' },
  { code: 'DTW', city: 'Detroit', airport: 'Detroit Metropolitan Wayne County', country: 'United States' },
  { code: 'MSY', city: 'New Orleans', airport: 'Louis Armstrong New Orleans International', country: 'United States' },
  { code: 'MEM', city: 'Memphis', airport: 'Memphis International', country: 'United States' },
  { code: 'BNA', city: 'Nashville', airport: 'Nashville International', country: 'United States' },
  { code: 'SLC', city: 'Salt Lake City', airport: 'Salt Lake City International', country: 'United States' },
  { code: 'PDX', city: 'Portland', airport: 'Portland International', country: 'United States' },
  { code: 'SAN', city: 'San Diego', airport: 'San Diego International', country: 'United States' },
  { code: 'SMF', city: 'Sacramento', airport: 'Sacramento International', country: 'United States' },
  { code: 'RNO', city: 'Reno', airport: 'Reno-Tahoe International', country: 'United States' },
  { code: 'TUS', city: 'Tucson', airport: 'Tucson International', country: 'United States' },
  { code: 'ABQ', city: 'Albuquerque', airport: 'Albuquerque International Sunport', country: 'United States' },
  { code: 'OKC', city: 'Oklahoma City', airport: 'Will Rogers World Airport', country: 'United States' },
  { code: 'TUL', city: 'Tulsa', airport: 'Tulsa International', country: 'United States' },
  { code: 'ICT', city: 'Wichita', airport: 'Wichita Dwight D. Eisenhower National', country: 'United States' },
  { code: 'OMA', city: 'Omaha', airport: 'Eppley Airfield', country: 'United States' },
  { code: 'MCI', city: 'Kansas City', airport: 'Kansas City International', country: 'United States' },
  { code: 'STL', city: 'St. Louis', airport: 'Lambert-St. Louis International', country: 'United States' },
  { code: 'IND', city: 'Indianapolis', airport: 'Indianapolis International', country: 'United States' },
  { code: 'CVG', city: 'Cincinnati', airport: 'Cincinnati/Northern Kentucky International', country: 'United States' },
  { code: 'CLE', city: 'Cleveland', airport: 'Cleveland Hopkins International', country: 'United States' },
  { code: 'CMH', city: 'Columbus', airport: 'John Glenn Columbus International', country: 'United States' },
  { code: 'PIT', city: 'Pittsburgh', airport: 'Pittsburgh International', country: 'United States' },
  { code: 'BUF', city: 'Buffalo', airport: 'Buffalo Niagara International', country: 'United States' },
  { code: 'ROC', city: 'Rochester', airport: 'Greater Rochester International', country: 'United States' },
  { code: 'SYR', city: 'Syracuse', airport: 'Syracuse Hancock International', country: 'United States' },
  { code: 'ALB', city: 'Albany', airport: 'Albany International', country: 'United States' },
  { code: 'PVD', city: 'Providence', airport: 'T.F. Green Airport', country: 'United States' },
  { code: 'BDL', city: 'Hartford', airport: 'Bradley International', country: 'United States' },
  { code: 'PHL', city: 'Philadelphia', airport: 'Philadelphia International', country: 'United States' },
  { code: 'RIC', city: 'Richmond', airport: 'Richmond International', country: 'United States' },
  { code: 'ORF', city: 'Norfolk', airport: 'Norfolk International', country: 'United States' },
  { code: 'RDU', city: 'Raleigh', airport: 'Raleigh-Durham International', country: 'United States' },
  { code: 'GSO', city: 'Greensboro', airport: 'Piedmont Triad International', country: 'United States' },
  { code: 'CHS', city: 'Charleston', airport: 'Charleston International', country: 'United States' },
  { code: 'CAE', city: 'Columbia', airport: 'Columbia Metropolitan', country: 'United States' },
  { code: 'SAV', city: 'Savannah', airport: 'Savannah/Hilton Head International', country: 'United States' },
  { code: 'JAX', city: 'Jacksonville', airport: 'Jacksonville International', country: 'United States' },
  { code: 'TPA', city: 'Tampa', airport: 'Tampa International', country: 'United States' },
  { code: 'RSW', city: 'Fort Myers', airport: 'Southwest Florida International', country: 'United States' },
  { code: 'PGD', city: 'Punta Gorda', airport: 'Punta Gorda Airport', country: 'United States' },
  { code: 'SRQ', city: 'Sarasota', airport: 'Sarasota-Bradenton International', country: 'United States' },
  
  // US Territories
  { code: 'STT', city: 'Charlotte Amalie', airport: 'Cyril E. King Airport', country: 'US Virgin Islands' },
  { code: 'STX', city: 'Christiansted', airport: 'Henry E. Rohlsen Airport', country: 'US Virgin Islands' },
  { code: 'GUM', city: 'Guam', airport: 'Antonio B. Won Pat International Airport', country: 'Guam' },
  { code: 'PPG', city: 'Pago Pago', airport: 'Pago Pago International Airport', country: 'American Samoa' },
  
  // Canada
  { code: 'YYZ', city: 'Toronto', airport: 'Pearson International', country: 'Canada' },
  { code: 'YTZ', city: 'Toronto', airport: 'Billy Bishop Toronto City', country: 'Canada' },
  { code: 'YVR', city: 'Vancouver', airport: 'Vancouver International', country: 'Canada' },
  { code: 'YUL', city: 'Montreal', airport: 'Pierre Elliott Trudeau International', country: 'Canada' },
  { code: 'YOW', city: 'Ottawa', airport: 'Macdonald-Cartier International', country: 'Canada' },
  { code: 'YYC', city: 'Calgary', airport: 'Calgary International', country: 'Canada' },
  { code: 'YEG', city: 'Edmonton', airport: 'Edmonton International', country: 'Canada' },
  { code: 'YWG', city: 'Winnipeg', airport: 'James Armstrong Richardson International', country: 'Canada' },
  { code: 'YHZ', city: 'Halifax', airport: 'Halifax Stanfield International', country: 'Canada' },
  { code: 'YQB', city: 'Quebec City', airport: 'Jean Lesage International', country: 'Canada' },
  { code: 'YDF', city: 'Deer Lake', airport: 'Deer Lake Regional Airport', country: 'Canada' },
  { code: 'YYT', city: 'St. John\'s', airport: 'St. John\'s International', country: 'Canada' },
  
  // Europe - United Kingdom
  { code: 'LHR', city: 'London', airport: 'Heathrow', country: 'United Kingdom' },
  { code: 'LGW', city: 'London', airport: 'Gatwick', country: 'United Kingdom' },
  { code: 'STN', city: 'London', airport: 'Stansted', country: 'United Kingdom' },
  { code: 'LTN', city: 'London', airport: 'Luton', country: 'United Kingdom' },
  { code: 'LCY', city: 'London', airport: 'London City', country: 'United Kingdom' },
  { code: 'MAN', city: 'Manchester', airport: 'Manchester Airport', country: 'United Kingdom' },
  { code: 'BHX', city: 'Birmingham', airport: 'Birmingham Airport', country: 'United Kingdom' },
  { code: 'EDI', city: 'Edinburgh', airport: 'Edinburgh Airport', country: 'United Kingdom' },
  { code: 'GLA', city: 'Glasgow', airport: 'Glasgow International', country: 'United Kingdom' },
  { code: 'BRS', city: 'Bristol', airport: 'Bristol Airport', country: 'United Kingdom' },
  { code: 'LPL', city: 'Liverpool', airport: 'John Lennon Airport', country: 'United Kingdom' },
  { code: 'NCL', city: 'Newcastle', airport: 'Newcastle International', country: 'United Kingdom' },
  { code: 'LBA', city: 'Leeds', airport: 'Leeds Bradford Airport', country: 'United Kingdom' },
  { code: 'CWL', city: 'Cardiff', airport: 'Cardiff Airport', country: 'United Kingdom' },
  { code: 'BFS', city: 'Belfast', airport: 'George Best Belfast City Airport', country: 'United Kingdom' },
  
  // Europe - France
  { code: 'CDG', city: 'Paris', airport: 'Charles de Gaulle', country: 'France' },
  { code: 'ORY', city: 'Paris', airport: 'Orly', country: 'France' },
  { code: 'BVA', city: 'Paris', airport: 'Beauvais', country: 'France' },
  { code: 'NCE', city: 'Nice', airport: 'Côte d\'Azur', country: 'France' },
  { code: 'LYS', city: 'Lyon', airport: 'Lyon-Saint-Exupéry', country: 'France' },
  { code: 'MRS', city: 'Marseille', airport: 'Marseille Provence', country: 'France' },
  { code: 'TLS', city: 'Toulouse', airport: 'Toulouse-Blagnac', country: 'France' },
  { code: 'BOD', city: 'Bordeaux', airport: 'Bordeaux-Mérignac', country: 'France' },
  { code: 'NTE', city: 'Nantes', airport: 'Nantes Atlantique', country: 'France' },
  { code: 'SXB', city: 'Strasbourg', airport: 'Strasbourg Airport', country: 'France' },
  
  // Europe - Germany
  { code: 'FRA', city: 'Frankfurt', airport: 'Frankfurt am Main', country: 'Germany' },
  { code: 'MUC', city: 'Munich', airport: 'Munich Airport', country: 'Germany' },
  { code: 'BER', city: 'Berlin', airport: 'Brandenburg Airport', country: 'Germany' },
  { code: 'DUS', city: 'Düsseldorf', airport: 'Düsseldorf Airport', country: 'Germany' },
  { code: 'HAM', city: 'Hamburg', airport: 'Hamburg Airport', country: 'Germany' },
  { code: 'CGN', city: 'Cologne', airport: 'Cologne Bonn Airport', country: 'Germany' },
  { code: 'STR', city: 'Stuttgart', airport: 'Stuttgart Airport', country: 'Germany' },
  { code: 'HAJ', city: 'Hanover', airport: 'Hannover Airport', country: 'Germany' },
  { code: 'NUE', city: 'Nuremberg', airport: 'Nuremberg Airport', country: 'Germany' },
  { code: 'DRS', city: 'Dresden', airport: 'Dresden Airport', country: 'Germany' },
  
  // Europe - Spain
  { code: 'MAD', city: 'Madrid', airport: 'Adolfo Suárez Madrid-Barajas', country: 'Spain' },
  { code: 'BCN', city: 'Barcelona', airport: 'Barcelona-El Prat', country: 'Spain' },
  { code: 'AGP', city: 'Malaga', airport: 'Málaga Airport', country: 'Spain' },
  { code: 'PMI', city: 'Palma', airport: 'Palma de Mallorca Airport', country: 'Spain' },
  { code: 'VLC', city: 'Valencia', airport: 'Valencia Airport', country: 'Spain' },
  { code: 'BIO', city: 'Bilbao', airport: 'Bilbao Airport', country: 'Spain' },
  { code: 'SVQ', city: 'Seville', airport: 'Sevilla Airport', country: 'Spain' },
  { code: 'LPA', city: 'Las Palmas', airport: 'Gran Canaria Airport', country: 'Spain' },
  { code: 'TFS', city: 'Tenerife', airport: 'Tenerife South Airport', country: 'Spain' },
  { code: 'IBZ', city: 'Ibiza', airport: 'Ibiza Airport', country: 'Spain' },
  
  // Europe - Italy
  { code: 'FCO', city: 'Rome', airport: 'Leonardo da Vinci International', country: 'Italy' },
  { code: 'CIA', city: 'Rome', airport: 'Ciampino Airport', country: 'Italy' },
  { code: 'MXP', city: 'Milan', airport: 'Malpensa Airport', country: 'Italy' },
  { code: 'LIN', city: 'Milan', airport: 'Linate Airport', country: 'Italy' },
  { code: 'BGY', city: 'Milan', airport: 'Orio al Serio Airport', country: 'Italy' },
  { code: 'VCE', city: 'Venice', airport: 'Marco Polo Airport', country: 'Italy' },
  { code: 'NAP', city: 'Naples', airport: 'Naples International', country: 'Italy' },
  { code: 'FLR', city: 'Florence', airport: 'Amerigo Vespucci Airport', country: 'Italy' },
  { code: 'BLQ', city: 'Bologna', airport: 'Guglielmo Marconi Airport', country: 'Italy' },
  { code: 'CTA', city: 'Catania', airport: 'Catania-Fontanarossa Airport', country: 'Italy' },
  
  // Europe - Netherlands
  { code: 'AMS', city: 'Amsterdam', airport: 'Schiphol', country: 'Netherlands' },
  { code: 'EIN', city: 'Eindhoven', airport: 'Eindhoven Airport', country: 'Netherlands' },
  { code: 'RTM', city: 'Rotterdam', airport: 'Rotterdam The Hague Airport', country: 'Netherlands' },
  { code: 'GRQ', city: 'Groningen', airport: 'Groningen Airport Eelde', country: 'Netherlands' },
  
  // Europe - Switzerland
  { code: 'ZRH', city: 'Zurich', airport: 'Zurich Airport', country: 'Switzerland' },
  { code: 'GVA', city: 'Geneva', airport: 'Geneva Airport', country: 'Switzerland' },
  { code: 'BSL', city: 'Basel', airport: 'EuroAirport Basel-Mulhouse-Freiburg', country: 'Switzerland' },
  { code: 'BRN', city: 'Bern', airport: 'Bern Airport', country: 'Switzerland' },
  
  // Europe - Austria
  { code: 'VIE', city: 'Vienna', airport: 'Vienna International Airport', country: 'Austria' },
  { code: 'SZG', city: 'Salzburg', airport: 'Salzburg Airport', country: 'Austria' },
  { code: 'INN', city: 'Innsbruck', airport: 'Innsbruck Airport', country: 'Austria' },
  { code: 'GRZ', city: 'Graz', airport: 'Graz Airport', country: 'Austria' },
  
  // Europe - Belgium
  { code: 'BRU', city: 'Brussels', airport: 'Brussels Airport', country: 'Belgium' },
  { code: 'CRL', city: 'Brussels', airport: 'Brussels South Charleroi Airport', country: 'Belgium' },
  { code: 'ANR', city: 'Antwerp', airport: 'Antwerp International Airport', country: 'Belgium' },
  
  // Europe - Scandinavia
  { code: 'ARN', city: 'Stockholm', airport: 'Stockholm Arlanda Airport', country: 'Sweden' },
  { code: 'GOT', city: 'Gothenburg', airport: 'Gothenburg Landvetter Airport', country: 'Sweden' },
  { code: 'MMX', city: 'Malmö', airport: 'Malmö Airport', country: 'Sweden' },
  { code: 'CPH', city: 'Copenhagen', airport: 'Copenhagen Airport', country: 'Denmark' },
  { code: 'AAL', city: 'Aalborg', airport: 'Aalborg Airport', country: 'Denmark' },
  { code: 'OSL', city: 'Oslo', airport: 'Oslo Gardermoen Airport', country: 'Norway' },
  { code: 'BGO', city: 'Bergen', airport: 'Bergen Airport', country: 'Norway' },
  { code: 'TRD', city: 'Trondheim', airport: 'Trondheim Airport', country: 'Norway' },
  { code: 'HEL', city: 'Helsinki', airport: 'Helsinki-Vantaa Airport', country: 'Finland' },
  { code: 'TMP', city: 'Tampere', airport: 'Tampere-Pirkkala Airport', country: 'Finland' },
  { code: 'KEF', city: 'Reykjavik', airport: 'Keflavík International Airport', country: 'Iceland' },
  
  // Europe - Eastern Europe
  { code: 'WAW', city: 'Warsaw', airport: 'Warsaw Chopin Airport', country: 'Poland' },
  { code: 'KRK', city: 'Krakow', airport: 'John Paul II International Airport Kraków-Balice', country: 'Poland' },
  { code: 'GDN', city: 'Gdansk', airport: 'Gdańsk Lech Wałęsa Airport', country: 'Poland' },
  { code: 'PRG', city: 'Prague', airport: 'Václav Havel Airport Prague', country: 'Czech Republic' },
  { code: 'BUD', city: 'Budapest', airport: 'Budapest Ferenc Liszt International Airport', country: 'Hungary' },
  { code: 'OTP', city: 'Bucharest', airport: 'Henri Coandă International Airport', country: 'Romania' },
  { code: 'SOF', city: 'Sofia', airport: 'Sofia Airport', country: 'Bulgaria' },
  { code: 'ZAG', city: 'Zagreb', airport: 'Franjo Tuđman Airport', country: 'Croatia' },
  { code: 'SPU', city: 'Split', airport: 'Split Airport', country: 'Croatia' },
  { code: 'DBV', city: 'Dubrovnik', airport: 'Dubrovnik Airport', country: 'Croatia' },
  
  // Europe - Portugal
  { code: 'LIS', city: 'Lisbon', airport: 'Humberto Delgado Airport', country: 'Portugal' },
  { code: 'OPO', city: 'Porto', airport: 'Francisco de Sá Carneiro Airport', country: 'Portugal' },
  { code: 'FAO', city: 'Faro', airport: 'Faro Airport', country: 'Portugal' },
  
  // Europe - Greece
  { code: 'ATH', city: 'Athens', airport: 'Athens International Airport', country: 'Greece' },
  { code: 'SKG', city: 'Thessaloniki', airport: 'Thessaloniki Airport', country: 'Greece' },
  { code: 'HER', city: 'Heraklion', airport: 'Heraklion Airport', country: 'Greece' },
  { code: 'RHO', city: 'Rhodes', airport: 'Rhodes International Airport', country: 'Greece' },
  { code: 'JTR', city: 'Santorini', airport: 'Santorini Airport', country: 'Greece' },
  { code: 'MYK', city: 'Mykonos', airport: 'Mykonos Airport', country: 'Greece' },

  // Europe - Ireland
  { code: 'DUB', city: 'Dublin', airport: 'Dublin Airport', country: 'Ireland' },
  { code: 'ORK', city: 'Cork', airport: 'Cork Airport', country: 'Ireland' },
  { code: 'SNN', city: 'Shannon', airport: 'Shannon Airport', country: 'Ireland' },
  
  // Europe - Russia
  { code: 'SVO', city: 'Moscow', airport: 'Sheremetyevo International Airport', country: 'Russia' },
  { code: 'DME', city: 'Moscow', airport: 'Domodedovo International Airport', country: 'Russia' },
  { code: 'VKO', city: 'Moscow', airport: 'Vnukovo International Airport', country: 'Russia' },
  { code: 'LED', city: 'St. Petersburg', airport: 'Pulkovo Airport', country: 'Russia' },
  
  // Asia Pacific - Japan
  { code: 'NRT', city: 'Tokyo', airport: 'Narita International', country: 'Japan' },
  { code: 'HND', city: 'Tokyo', airport: 'Haneda Airport', country: 'Japan' },
  { code: 'KIX', city: 'Osaka', airport: 'Kansai International Airport', country: 'Japan' },
  { code: 'ITM', city: 'Osaka', airport: 'Osaka International Airport', country: 'Japan' },
  { code: 'NGO', city: 'Nagoya', airport: 'Chubu Centrair International Airport', country: 'Japan' },
  { code: 'FUK', city: 'Fukuoka', airport: 'Fukuoka Airport', country: 'Japan' },
  { code: 'CTS', city: 'Sapporo', airport: 'New Chitose Airport', country: 'Japan' }, // Corrected from SPK to CTS
  { code: 'HIJ', city: 'Hiroshima', airport: 'Hiroshima Airport', country: 'Japan' },
  { code: 'KMJ', city: 'Kumamoto', airport: 'Kumamoto Airport', country: 'Japan' },
  { code: 'OKA', city: 'Okinawa', airport: 'Naha Airport', country: 'Japan' },
  
  // Asia Pacific - South Korea
  { code: 'ICN', city: 'Seoul', airport: 'Incheon International', country: 'South Korea' },
  { code: 'GMP', city: 'Seoul', airport: 'Gimpo Airport', country: 'South Korea' },
  { code: 'PUS', city: 'Busan', airport: 'Gimhae International Airport', country: 'South Korea' },
  { code: 'CJU', city: 'Jeju', airport: 'Jeju International Airport', country: 'South Korea' },
  { code: 'TAE', city: 'Daegu', airport: 'Daegu International Airport', country: 'South Korea' },
  
  // Asia Pacific - China
  { code: 'PEK', city: 'Beijing', airport: 'Beijing Capital International', country: 'China' },
  { code: 'PKX', city: 'Beijing', airport: 'Beijing Daxing International', country: 'China' },
  { code: 'PVG', city: 'Shanghai', airport: 'Shanghai Pudong International', country: 'China' },
  { code: 'SHA', city: 'Shanghai', airport: 'Shanghai Hongqiao International', country: 'China' },
  { code: 'CAN', city: 'Guangzhou', airport: 'Guangzhou Baiyun International', country: 'China' },
  { code: 'SZX', city: 'Shenzhen', airport: 'Shenzhen Bao\'an International', country: 'China' },
  { code: 'CTU', city: 'Chengdu', airport: 'Chengdu Shuangliu International', country: 'China' },
  { code: 'XIY', city: 'Xi\'an', airport: 'Xi\'an Xianyang International', country: 'China' },
  { code: 'NKG', city: 'Nanjing', airport: 'Nanjing Lukou International', country: 'China' },
  { code: 'HGH', city: 'Hangzhou', airport: 'Hangzhou Xiaoshan International', country: 'China' },
  
  // Asia Pacific - Southeast Asia
  { code: 'SIN', city: 'Singapore', airport: 'Changi Airport', country: 'Singapore' },
  { code: 'KUL', city: 'Kuala Lumpur', airport: 'Kuala Lumpur International', country: 'Malaysia' },
  { code: 'SZB', city: 'Kuala Lumpur', airport: 'Sultan Abdul Aziz Shah Airport', country: 'Malaysia' },
  { code: 'PEN', city: 'Penang', airport: 'Penang International Airport', country: 'Malaysia' },
  { code: 'BKK', city: 'Bangkok', airport: 'Suvarnabhumi Airport', country: 'Thailand' },
  { code: 'DMK', city: 'Bangkok', airport: 'Don Mueang International Airport', country: 'Thailand' },
  { code: 'HKT', city: 'Phuket', airport: 'Phuket International Airport', country: 'Thailand' },
  { code: 'CNX', city: 'Chiang Mai', airport: 'Chiang Mai International Airport', country: 'Thailand' },
  { code: 'SGN', city: 'Ho Chi Minh City', airport: 'Tan Son Nhat Airport', country: 'Vietnam' },
  { code: 'HAN', city: 'Hanoi', airport: 'Noi Bai International Airport', country: 'Vietnam' },
  { code: 'DAD', city: 'Da Nang', airport: 'Da Nang International Airport', country: 'Vietnam' },
  { code: 'CGK', city: 'Jakarta', airport: 'Soekarno-Hatta International Airport', country: 'Indonesia' },
  { code: 'DPS', city: 'Bali', airport: 'Ngurah Rai International Airport', country: 'Indonesia' },
  // Philippines - International Airports
  { code: 'MNL', city: 'Manila', airport: 'Ninoy Aquino International Airport', country: 'Philippines' },
  { code: 'CEB', city: 'Cebu', airport: 'Mactan-Cebu International Airport', country: 'Philippines' },
  { code: 'CRK', city: 'Angeles City', airport: 'Clark International Airport', country: 'Philippines' },
  { code: 'DVO', city: 'Davao City', airport: 'Francisco Bangoy International Airport', country: 'Philippines' },
  { code: 'ILO', city: 'Iloilo City', airport: 'Iloilo International Airport', country: 'Philippines' },
  { code: 'PPS', city: 'Puerto Princesa', airport: 'Puerto Princesa International Airport', country: 'Philippines' },
  { code: 'KLO', city: 'Kalibo', airport: 'Kalibo International Airport', country: 'Philippines' },
  { code: 'CGY', city: 'Cagayan de Oro', airport: 'Laguindingan International Airport', country: 'Philippines' },
  { code: 'BCD', city: 'Bacolod City', airport: 'Bacolod-Silay International Airport', country: 'Philippines' },
  { code: 'GES', city: 'General Santos City', airport: 'General Santos International Airport', country: 'Philippines' },
  { code: 'ZAM', city: 'Zamboanga City', airport: 'Zamboanga International Airport', country: 'Philippines' },
  { code: 'LAO', city: 'Laoag City', airport: 'Laoag International Airport', country: 'Philippines' },
  { code: 'SFS', city: 'Subic Bay', airport: 'Subic Bay International Airport', country: 'Philippines' },
  { code: 'DRP', city: 'Legazpi City', airport: 'Bicol International Airport', country: 'Philippines' },
  { code: 'TAG', city: 'Panglao', airport: 'Bohol-Panglao International Airport', country: 'Philippines' },
  
  // Philippines - Major Domestic Airports
  { code: 'BXU', city: 'Butuan City', airport: 'Bancasi Airport', country: 'Philippines' },
  { code: 'CBO', city: 'Cotabato City', airport: 'Awang Airport', country: 'Philippines' },
  { code: 'TAC', city: 'Tacloban City', airport: 'Daniel Z. Romualdez Airport', country: 'Philippines' },
  { code: 'DPL', city: 'Dipolog City', airport: 'Dipolog Airport', country: 'Philippines' },
  { code: 'DGT', city: 'Dumaguete City', airport: 'Dumaguete-Sibulan Airport', country: 'Philippines' },
  { code: 'WNP', city: 'Naga City', airport: 'Naga Airport', country: 'Philippines' },
  { code: 'PAG', city: 'Pagadian City', airport: 'Pagadian Airport', country: 'Philippines' },
  { code: 'RXS', city: 'Roxas City', airport: 'Roxas Airport', country: 'Philippines' },
  { code: 'OZC', city: 'Ozamiz City', airport: 'Labo Airport', country: 'Philippines' },
  { code: 'SUG', city: 'Surigao City', airport: 'Surigao Airport', country: 'Philippines' },
  { code: 'TWT', city: 'Bongao', airport: 'Sanga-Sanga Airport', country: 'Philippines' },
  { code: 'MPH', city: 'Malay', airport: 'Godofredo P. Ramos Airport (Caticlan/Boracay)', country: 'Philippines' },
  { code: 'CYZ', city: 'Cauayan City', airport: 'Cauayan Airport', country: 'Philippines' },
  { code: 'SJI', city: 'San Jose', airport: 'San Jose Airport', country: 'Philippines' },
  { code: 'TUG', city: 'Tuguegarao City', airport: 'Tuguegarao Airport', country: 'Philippines' },
  { code: 'BSO', city: 'Basco', airport: 'Basco Airport', country: 'Philippines' },
  { code: 'CYP', city: 'Calbayog City', airport: 'Calbayog Airport', country: 'Philippines' },
  { code: 'CGM', city: 'Mambajao', airport: 'Camiguin Airport', country: 'Philippines' },
  { code: 'CRM', city: 'Catarman', airport: 'Catarman National Airport', country: 'Philippines' },
  { code: 'USU', city: 'Coron', airport: 'Francisco B. Reyes Airport (Busuanga)', country: 'Philippines' },
  { code: 'MBT', city: 'Masbate City', airport: 'Masbate Airport', country: 'Philippines' },
  { code: 'IAO', city: 'Siargao', airport: 'Sayak Airport', country: 'Philippines' },
  { code: 'VRC', city: 'Virac', airport: 'Virac Airport', country: 'Philippines' },
  
  // Asia Pacific - Hong Kong & Taiwan
  { code: 'HKG', city: 'Hong Kong', airport: 'Hong Kong International', country: 'Hong Kong' },
  { code: 'TPE', city: 'Taipei', airport: 'Taiwan Taoyuan International', country: 'Taiwan' },
  { code: 'TSA', city: 'Taipei', airport: 'Songshan Airport', country: 'Taiwan' },
  { code: 'KHH', city: 'Kaohsiung', airport: 'Kaohsiung International Airport', country: 'Taiwan' },
  
  // Asia Pacific - India
  { code: 'DEL', city: 'New Delhi', airport: 'Indira Gandhi International Airport', country: 'India' },
  { code: 'BOM', city: 'Mumbai', airport: 'Chhatrapati Shivaji Maharaj International Airport', country: 'India' },
  { code: 'BLR', city: 'Bangalore', airport: 'Kempegowda International Airport', country: 'India' },
  { code: 'MAA', city: 'Chennai', airport: 'Chennai International Airport', country: 'India' },
  { code: 'HYD', city: 'Hyderabad', airport: 'Rajiv Gandhi International Airport', country: 'India' },
  { code: 'CCU', city: 'Kolkata', airport: 'Netaji Subhash Chandra Bose International Airport', country: 'India' },
  { code: 'AMD', city: 'Ahmedabad', airport: 'Sardar Vallabhbhai Patel International Airport', country: 'India' },
  { code: 'GOI', city: 'Goa', airport: 'Goa International Airport', country: 'India' },
  { code: 'COK', city: 'Kochi', airport: 'Cochin International Airport', country: 'India' },
  { code: 'TRV', city: 'Thiruvananthapuram', airport: 'Trivandrum International Airport', country: 'India' },
  
  // Asia Pacific - Australia & New Zealand
  { code: 'SYD', city: 'Sydney', airport: 'Kingsford Smith', country: 'Australia' },
  { code: 'MEL', city: 'Melbourne', airport: 'Melbourne Airport', country: 'Australia' },
  { code: 'BNE', city: 'Brisbane', airport: 'Brisbane Airport', country: 'Australia' },
  { code: 'PER', city: 'Perth', airport: 'Perth Airport', country: 'Australia' },
  { code: 'ADL', city: 'Adelaide', airport: 'Adelaide Airport', country: 'Australia' },
  { code: 'DRW', city: 'Darwin', airport: 'Darwin Airport', country: 'Australia' },
  { code: 'CNS', city: 'Cairns', airport: 'Cairns Airport', country: 'Australia' },
  { code: 'OOL', city: 'Gold Coast', airport: 'Gold Coast Airport', country: 'Australia' }, // Corrected from GC to OOL
  { code: 'AKL', city: 'Auckland', airport: 'Auckland Airport', country: 'New Zealand' },
  { code: 'CHC', city: 'Christchurch', airport: 'Christchurch Airport', country: 'New Zealand' },
  { code: 'WLG', city: 'Wellington', airport: 'Wellington Airport', country: 'New Zealand' },
  
  // Middle East & Africa
  { code: 'DXB', city: 'Dubai', airport: 'Dubai International', country: 'UAE' },
  { code: 'DWC', city: 'Dubai', airport: 'Al Maktoum International', country: 'UAE' },
  { code: 'AUH', city: 'Abu Dhabi', airport: 'Abu Dhabi International', country: 'UAE' },
  { code: 'DOH', city: 'Doha', airport: 'Hamad International', country: 'Qatar' },
  { code: 'KWI', city: 'Kuwait City', airport: 'Kuwait International Airport', country: 'Kuwait' },
  { code: 'RUH', city: 'Riyadh', airport: 'King Khalid International Airport', country: 'Saudi Arabia' },
  { code: 'JED', city: 'Jeddah', airport: 'King Abdulaziz International Airport', country: 'Saudi Arabia' },
  { code: 'AMM', city: 'Amman', airport: 'Queen Alia International Airport', country: 'Jordan' },
  { code: 'BEY', city: 'Beirut', airport: 'Rafic Hariri International Airport', country: 'Lebanon' },
  { code: 'CAI', city: 'Cairo', airport: 'Cairo International', country: 'Egypt' },
  { code: 'SSH', city: 'Sharm el-Sheikh', airport: 'Sharm el-Sheikh International Airport', country: 'Egypt' },
  { code: 'HRG', city: 'Hurghada', airport: 'Hurghada International Airport', country: 'Egypt' },
  { code: 'JNB', city: 'Johannesburg', airport: 'O.R. Tambo International', country: 'South Africa' },
  { code: 'CPT', city: 'Cape Town', airport: 'Cape Town International', country: 'South Africa' },
  { code: 'DUR', city: 'Durban', airport: 'King Shaka International Airport', country: 'South Africa' },
  { code: 'LOS', city: 'Lagos', airport: 'Murtala Muhammed International Airport', country: 'Nigeria' },
  { code: 'ABJ', city: 'Abidjan', airport: 'Félix-Houphouët-Boigny International Airport', country: 'Ivory Coast' },
  { code: 'ACC', city: 'Accra', airport: 'Kotoka International Airport', country: 'Ghana' },
  { code: 'CMN', city: 'Casablanca', airport: 'Mohammed V International Airport', country: 'Morocco' },
  { code: 'RAK', city: 'Marrakech', airport: 'Marrakech Menara Airport', country: 'Morocco' },
  { code: 'TUN', city: 'Tunis', airport: 'Tunis-Carthage International Airport', country: 'Tunisia' },
  { code: 'ALG', city: 'Algiers', airport: 'Houari Boumediene Airport', country: 'Algeria' },
  
  // South America
  { code: 'GRU', city: 'São Paulo', airport: 'Guarulhos International', country: 'Brazil' },
  { code: 'CGH', city: 'São Paulo', airport: 'Congonhas Airport', country: 'Brazil' },
  { code: 'GIG', city: 'Rio de Janeiro', airport: 'Galeão International', country: 'Brazil' },
  { code: 'SDU', city: 'Rio de Janeiro', airport: 'Santos Dumont Airport', country: 'Brazil' },
  { code: 'BSB', city: 'Brasília', airport: 'Presidente Juscelino Kubitschek International Airport', country: 'Brazil' },
  { code: 'SSA', city: 'Salvador', airport: 'Deputado Luís Eduardo Magalhães International Airport', country: 'Brazil' },
  { code: 'REC', city: 'Recife', airport: 'Recife/Guararapes–Gilberto Freyre International Airport', country: 'Brazil' },
  { code: 'FOR', city: 'Fortaleza', airport: 'Pinto Martins International Airport', country: 'Brazil' },
  { code: 'CNF', city: 'Belo Horizonte', airport: 'Tancredo Neves International Airport', country: 'Brazil' },
  { code: 'CWB', city: 'Curitiba', airport: 'Afonso Pena International Airport', country: 'Brazil' },
  { code: 'POA', city: 'Porto Alegre', airport: 'Salgado Filho International Airport', country: 'Brazil' },
  { code: 'EZE', city: 'Buenos Aires', airport: 'Ezeiza International', country: 'Argentina' },
  { code: 'AEP', city: 'Buenos Aires', airport: 'Jorge Newbery Airfield', country: 'Argentina' },
  { code: 'COR', city: 'Córdoba', airport: 'Córdoba Airport', country: 'Argentina' },
  { code: 'MDZ', city: 'Mendoza', airport: 'Governor Francisco Gabrielli International Airport', country: 'Argentina' },
  { code: 'SCL', city: 'Santiago', airport: 'Arturo Merino Benítez International Airport', country: 'Chile' },
  { code: 'LIM', city: 'Lima', airport: 'Jorge Chávez International', country: 'Peru' },
  { code: 'CUZ', city: 'Cusco', airport: 'Alejandro Velasco Astete International Airport', country: 'Peru' },
  { code: 'BOG', city: 'Bogotá', airport: 'El Dorado International Airport', country: 'Colombia' },
  { code: 'MDE', city: 'Medellín', airport: 'José María Córdova International Airport', country: 'Colombia' },
  { code: 'CLO', city: 'Cali', airport: 'Alfonso Bonilla Aragón International Airport', country: 'Colombia' },
  { code: 'CCS', city: 'Caracas', airport: 'Simón Bolívar International Airport', country: 'Venezuela' },
  { code: 'UIO', city: 'Quito', airport: 'Mariscal Sucre International Airport', country: 'Ecuador' },
  { code: 'GYE', city: 'Guayaquil', airport: 'José Joaquín de Olmedo International Airport', country: 'Ecuador' },
  { code: 'LPB', city: 'La Paz', airport: 'El Alto International Airport', country: 'Bolivia' },
  { code: 'VVI', city: 'Santa Cruz', airport: 'Viru Viru International Airport', country: 'Bolivia' },
  { code: 'ASU', city: 'Asunción', airport: 'Silvio Pettirossi International Airport', country: 'Paraguay' },
  { code: 'MVD', city: 'Montevideo', airport: 'Carrasco International Airport', country: 'Uruguay' },
  { code: 'GEO', city: 'Georgetown', airport: 'Cheddi Jagan International Airport', country: 'Guyana' },
  { code: 'PBM', city: 'Paramaribo', airport: 'Johan Adolf Pengel International Airport', country: 'Suriname' },
  
  // Central America & Caribbean
  { code: 'MEX', city: 'Mexico City', airport: 'Mexico City International Airport', country: 'Mexico' },
  { code: 'CUN', city: 'Cancún', airport: 'Cancún International Airport', country: 'Mexico' },
  { code: 'GDL', city: 'Guadalajara', airport: 'Miguel Hidalgo y Costilla Guadalajara International Airport', country: 'Mexico' },
  { code: 'PVR', city: 'Puerto Vallarta', airport: 'Licenciado Gustavo Díaz Ordaz International Airport', country: 'Mexico' },
  { code: 'SJD', city: 'Los Cabos', airport: 'Los Cabos International Airport', country: 'Mexico' },
  { code: 'MTY', city: 'Monterrey', airport: 'General Mariano Escobedo International Airport', country: 'Mexico' },
  { code: 'TIJ', city: 'Tijuana', airport: 'General Abelardo L. Rodríguez International Airport', country: 'Mexico' },
  { code: 'MZT', city: 'Mazatlán', airport: 'General Rafael Buelna International Airport', country: 'Mexico' },
  { code: 'ACA', city: 'Acapulco', airport: 'General Juan N. Álvarez International Airport', country: 'Mexico' },
  { code: 'CZM', city: 'Cozumel', airport: 'Cozumel International Airport', country: 'Mexico' },
  { code: 'GUA', city: 'Guatemala City', airport: 'La Aurora International Airport', country: 'Guatemala' },
  { code: 'SAL', city: 'San Salvador', airport: 'Monseñor Óscar Arnulfo Romero International Airport', country: 'El Salvador' },
  { code: 'TGU', city: 'Tegucigalpa', airport: 'Toncontín International Airport', country: 'Honduras' },
  { code: 'MGA', city: 'Managua', airport: 'Augusto C. Sandino International Airport', country: 'Nicaragua' },
  { code: 'SJO', city: 'San José', airport: 'Juan Santamaría International Airport', country: 'Costa Rica' },
  { code: 'LIR', city: 'Liberia', airport: 'Daniel Oduber Quirós International Airport', country: 'Costa Rica' },
  { code: 'PTY', city: 'Panama City', airport: 'Tocumen International Airport', country: 'Panama' },
  { code: 'HAV', city: 'Havana', airport: 'José Martí International Airport', country: 'Cuba' },
  { code: 'VRA', city: 'Varadero', airport: 'Juan Gualberto Gómez Airport', country: 'Cuba' },
  { code: 'SDQ', city: 'Santo Domingo', airport: 'Las Américas International Airport', country: 'Dominican Republic' },
  { code: 'PUJ', city: 'Punta Cana', airport: 'Punta Cana International Airport', country: 'Dominican Republic' },
  { code: 'SJU', city: 'San Juan', airport: 'Luis Muñoz Marín International Airport', country: 'Puerto Rico' },
  { code: 'PSE', city: 'Ponce', airport: 'Mercedita Airport', country: 'Puerto Rico' },
  { code: 'NAS', city: 'Nassau', airport: 'Lynden Pindling International Airport', country: 'Bahamas' },
  { code: 'KIN', city: 'Kingston', airport: 'Norman Manley International Airport', country: 'Jamaica' },
  { code: 'MBJ', city: 'Montego Bay', airport: 'Sangster International Airport', country: 'Jamaica' },
  { code: 'OCJ', city: 'Ocho Rios', airport: 'Ian Fleming International Airport', country: 'Jamaica' },
  { code: 'KTP', city: 'Kingston', airport: 'Tinson Pen Aerodrome', country: 'Jamaica' },
  { code: 'BGI', city: 'Bridgetown', airport: 'Grantley Adams International Airport', country: 'Barbados' },
  { code: 'UVF', city: 'Vieux Fort', airport: 'Hewanorra International Airport', country: 'Saint Lucia' },
  { code: 'SLU', city: 'Castries', airport: 'George F. L. Charles Airport', country: 'Saint Lucia' },
  { code: 'GND', city: 'St. George\'s', airport: 'Maurice Bishop International Airport', country: 'Grenada' },
  { code: 'SVD', city: 'Kingstown', airport: 'Argyle International Airport', country: 'Saint Vincent and the Grenadines' },
  { code: 'ANU', city: 'St. John\'s', airport: 'V. C. Bird International Airport', country: 'Antigua and Barbuda' },
  { code: 'POS', city: 'Port of Spain', airport: 'Piarco International Airport', country: 'Trinidad and Tobago' },
  { code: 'TAB', city: 'Scarborough', airport: 'A.N.R. Robinson International Airport', country: 'Trinidad and Tobago' },
  { code: 'AUA', city: 'Oranjestad', airport: 'Queen Beatrix International Airport', country: 'Aruba' },
  { code: 'CUR', city: 'Willemstad', airport: 'Hato International Airport', country: 'Curaçao' },
  { code: 'SXM', city: 'Philipsburg', airport: 'Princess Juliana International Airport', country: 'Sint Maarten' },
  
  // Pacific Islands
  { code: 'NAN', city: 'Nadi', airport: 'Nadi International Airport', country: 'Fiji' },
  { code: 'SUV', city: 'Suva', airport: 'Nausori Airport', country: 'Fiji' },
  { code: 'PPT', city: 'Papeete', airport: 'Faa\'a International Airport', country: 'French Polynesia' },
  { code: 'APW', city: 'Apia', airport: 'Faleolo International Airport', country: 'Samoa' },
  { code: 'NOU', city: 'Nouméa', airport: 'La Tontouta International Airport', country: 'New Caledonia' },
  { code: 'VLI', city: 'Port Vila', airport: 'Bauerfield International Airport', country: 'Vanuatu' },
  { code: 'TBU', city: 'Nuku\'alofa', airport: 'Fua\'amotu International Airport', country: 'Tonga' },
  { code: 'RAR', city: 'Avarua', airport: 'Rarotonga International Airport', country: 'Cook Islands' },
  
  // Additional Major Destinations
  { code: 'IST', city: 'Istanbul', airport: 'Istanbul Airport', country: 'Turkey' },
  { code: 'SAW', city: 'Istanbul', airport: 'Sabiha Gökçen International Airport', country: 'Turkey' },
  { code: 'ESB', city: 'Ankara', airport: 'Esenboğa Airport', country: 'Turkey' },
  { code: 'AYT', city: 'Antalya', airport: 'Antalya Airport', country: 'Turkey' },
  { code: 'TLV', city: 'Tel Aviv', airport: 'Ben Gurion Airport', country: 'Israel' },
  { code: 'IKA', city: 'Tehran', airport: 'Imam Khomeini International Airport', country: 'Iran' }
];

// Only airports search offers (src/data/airportOverrides.json can take one away).
const OFFERED_FALLBACK = FALLBACK_AIRPORTS.filter((a) => isOfferedCode(a.code));

/** Unique city names from supported airports - for search activity animation etc. */
export const AIRPORT_CITIES = [...new Set(OFFERED_FALLBACK.map((a) => a.city))];

// Shared result row for the desktop dropdown and mobile modal.
// `mobile` bumps padding/type for touch targets; visual language is
// identical so the two surfaces feel like the same control.
function AirportOption({ airport, onSelect, mobile = false }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(airport)}
      className={cn(
        'w-full text-left flex items-center gap-3 rounded-xl transition-colors',
        'hover:bg-white/[0.06] active:bg-white/[0.09] focus-visible:bg-white/[0.06] focus-visible:outline-none',
        mobile ? 'px-3 py-3' : 'px-2.5 py-2'
      )}
    >
      <div
        className={cn(
          'rounded-full bg-[hsl(228,40%,38%)]/[0.14] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center flex-shrink-0',
          mobile ? 'w-9 h-9' : 'w-7 h-7'
        )}
      >
        <Plane className={cn('text-[hsl(228,35%,58%)]', mobile ? 'w-4 h-4' : 'w-3 h-3')} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={cn('text-white font-medium truncate', mobile ? 'text-[15px]' : 'text-[13px]')}>
          {airport.city}
        </div>
        <div className={cn('text-white/40 font-normal truncate', mobile ? 'text-[12px] mt-0.5' : 'text-[11px]')}>
          {airport.nearbyFor ? `Near ${airport.nearbyFor} · ` : airport.servesName ? `Serves ${airport.servesName} · ` : ''}{airport.airport}, {airport.country}
        </div>
      </div>
      <span
        className={cn(
          'flex-shrink-0 text-[hsl(228,30%,72%)] bg-[hsl(228,40%,38%)]/[0.14] border border-[hsl(228,35%,58%)]/20 rounded-full font-medium tabular-nums tracking-wide',
          mobile ? 'text-[11px] px-2.5 py-1' : 'text-[10px] px-2 py-0.5'
        )}
      >
        {airport.code}
      </span>
    </button>
  );
}

export default function AirportInput({ 
  placeholder, 
  value, 
  onChange, 
  className,
  containerClassName,
  inputClassName,
  iconClassName,
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAirports, setFilteredAirports] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const loadAirportDisplay = async () => {
      if (value) {
        try {
          const airport = await Airport.getByCode(value);
          // City name only — the IATA code in parentheses read as
          // clutter in the pill. The dropdown still shows codes for
          // disambiguation (JFK vs LGA) at selection time.
          if (airport) {
            setSearchTerm(airport.city);
          } else {
            // Fallback to local search
            const fallbackAirport = OFFERED_FALLBACK.find(a => a.code === value);
            if (fallbackAirport) {
              setSearchTerm(fallbackAirport.city);
            } else {
              setSearchTerm(value);
            }
          }
        } catch (error) {
          console.error('Error loading airport:', error);
          setSearchTerm(value);
        }
      } else {
        setSearchTerm('');
      }
    };
    loadAirportDisplay();
  }, [value]);

  // The airport list is its own small download: start it when the field
  // opens, so the first letter typed already has it.
  useEffect(() => {
    if (isOpen) loadSearchAirports().catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    const searchAirports = async () => {
      if (isOpen && searchTerm.length >= 2) {
        try {
          const results = await Airport.search(searchTerm);
          setFilteredAirports(results.slice(0, 8));
        } catch (error) {
          console.error('Error searching airports:', error);
          // The list didn't load (offline, or a stale build): the built-in few.
          const term = searchTerm.toLowerCase();
          const filtered = OFFERED_FALLBACK.filter(airport =>
            airport.city.toLowerCase().includes(term) ||
            airport.code.toLowerCase().includes(term) ||
            airport.airport.toLowerCase().includes(term) ||
            airport.country.toLowerCase().includes(term)
          ).slice(0, 8);
          setFilteredAirports(filtered);
        }
      } else {
        setFilteredAirports([]);
      }
    };
    searchAirports();
  }, [searchTerm, isOpen]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelect = (airport) => {
    console.log('🔵 Airport clicked:', airport.city, airport.code);
    const displayValue = airport.city;
    
    // Update state and close modal
    setIsOpen(false);
    setSearchTerm(displayValue);
    onChange(airport.code); // Pass the airport code to parent
    
    // Blur the input to prevent reopening on mobile
    if (inputRef.current) {
      inputRef.current.blur();
    }
    
    console.log('✅ Modal closed, value set to:', displayValue);
  };

  const handleInputFocus = () => {
    if (disabled) return;
    setIsOpen(true);
  };

  const handleInputBlur = (e) => {
    // Don't close if clicking on dropdown (desktop only)
    if (!isMobile) {
        if (dropdownRef.current && dropdownRef.current.contains(e.relatedTarget)) {
            return;
        }
        setTimeout(() => setIsOpen(false), 200);
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    // Ensure input loses focus to prevent immediate reopening
    if (inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }, 100);
    }
  };

  return (
    <div className={cn("relative", containerClassName)}>
      <div className="relative">
        <MapPin className={cn(
          "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400",
          iconClassName
        )} />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={disabled ? undefined : handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className={cn(className, inputClassName, "pl-10", disabled && "opacity-60 cursor-not-allowed")}
          autoComplete="off"
          readOnly={isMobile || disabled}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          title={disabled ? 'Origin and destination are locked for a flight change' : undefined}
        />
      </div>
      
      {/* Desktop Dropdown */}
      {isOpen && !isMobile && (filteredAirports.length > 0 || searchTerm.length >= 2) && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-[9999] mt-2 min-w-[300px] rounded-2xl border border-white/10 bg-black/90 backdrop-blur-2xl shadow-[0_24px_64px_-16px_rgba(0,0,0,0.9)] overflow-hidden"
        >
          {filteredAirports.length > 0 ? (
            <div className="p-1.5 max-h-[min(50vh,420px)] overflow-y-auto">
              {filteredAirports.map((airport) => (
                <AirportOption key={airport.code} airport={airport} onSelect={handleSelect} />
              ))}
            </div>
          ) : (
            <div className="px-4 py-5 text-center">
              <p className="text-white/65 text-[13px] font-normal">No airports found</p>
              <p className="text-white/30 text-[11px] font-normal mt-0.5">Try a city name or airport code</p>
            </div>
          )}
        </div>
      )}

      {/* Mobile Full Screen Modal - using Portal to escape pointer-events-none parents */}
      {isOpen && isMobile && createPortal(
        <div className="fixed inset-0 z-[200] flex flex-col bg-black pointer-events-auto">
          {/* Header — eyebrow + light title, hairline-circled close,
              matching the product's masthead pattern. */}
          <div className="flex items-center justify-between px-5 pt-5 pb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(228,35%,58%)]/85 font-medium">
                Airports
              </p>
              <h3 className="text-xl font-light text-white tracking-tight mt-1">{placeholder}</h3>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseModal}
              className="h-9 w-9 rounded-full border border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:bg-white/[0.08]"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="px-5 pb-4">
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 z-10" />
              <Input
                type="text"
                placeholder={`Search ${placeholder.toLowerCase()}…`}
                value={searchTerm}
                onChange={handleInputChange}
                className="w-full h-12 pl-11 rounded-xl bg-white/[0.04] border-white/10 text-white text-base font-normal placeholder:text-white/35 focus-visible:ring-1 focus-visible:ring-[hsl(228,35%,58%)]/30 focus-visible:ring-offset-0 focus-visible:border-[hsl(228,35%,58%)]/30"
                autoComplete="off"
                autoFocus
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3.5 pb-32">
            {filteredAirports.length > 0 ? (
              <div className="space-y-0.5">
                {filteredAirports.map((airport) => (
                  <AirportOption key={airport.code} airport={airport} onSelect={handleSelect} mobile />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center text-center pt-16 px-6">
                <div className="w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center mb-3">
                  <Plane className="w-5 h-5 text-white/30" />
                </div>
                {searchTerm.length >= 2 ? (
                  <>
                    <p className="text-white/65 text-sm font-normal">No airports found</p>
                    <p className="text-white/30 text-xs font-normal mt-1">Try a city name or airport code</p>
                  </>
                ) : (
                  <>
                    <p className="text-white/65 text-sm font-normal">Where to?</p>
                    <p className="text-white/30 text-xs font-normal mt-1">Start typing a city, airport, or code</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
